import { type Locale } from "./i18n";

export const analyticsMeasurementId = "G-ETHW1SL1W5";
export const analyticsConsentStorageKey = "collectie-analytics-consent-v1";
export const analyticsCookieLifetimeDays = 90;
export const analyticsDefaultConsent = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;
export const analyticsConfig = {
  allow_ad_personalization_signals: false,
  allow_google_signals: false,
  cookie_expires: analyticsCookieLifetimeDays * 24 * 60 * 60,
  cookie_update: false,
  send_page_view: false,
} as const;

export type AnalyticsConsent = "granted" | "denied";

type GtagArguments = [command: string, ...values: unknown[]];

declare global {
  interface Window {
    dataLayer?: GtagArguments[];
    gtag?: (...args: GtagArguments) => void;
  }
}

let analyticsInitialized = false;
let pageViewSent = false;

export function getAnalyticsConsent(): AnalyticsConsent | null {
  try {
    const stored = window.localStorage.getItem(analyticsConsentStorageKey);
    return stored === "granted" || stored === "denied" ? stored : null;
  } catch {
    return null;
  }
}

function storeAnalyticsConsent(consent: AnalyticsConsent): void {
  try {
    window.localStorage.setItem(analyticsConsentStorageKey, consent);
  } catch {
    // The choice still applies to this page even when storage is unavailable.
  }
}

function gtag(...args: GtagArguments): void {
  window.dataLayer ??= [];
  window.dataLayer.push(args);
}

function sanitizedUrl(value: string, baseOrigin: string): string {
  if (!value) return "";
  try {
    const url = new URL(value, baseOrigin);
    return `${url.origin}${url.pathname}`;
  } catch {
    return "";
  }
}

export type AnalyticsPage = {
  pageTitle: string;
  pageLocation: string;
  pagePath: string;
  pageReferrer: string;
  language: Locale;
};

export function buildAnalyticsPage(
  location: Pick<Location, "origin" | "pathname">,
  title: string,
  referrer: string,
  locale: Locale,
): AnalyticsPage {
  return {
    pageTitle: title,
    pageLocation: `${location.origin}${location.pathname}`,
    pagePath: location.pathname,
    pageReferrer: sanitizedUrl(referrer, location.origin),
    language: locale,
  };
}

function appendGoogleTag(): void {
  if (document.querySelector('script[data-collectie-analytics="true"]')) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(analyticsMeasurementId)}`;
  script.referrerPolicy = "strict-origin-when-cross-origin";
  script.dataset.collectieAnalytics = "true";
  document.head.appendChild(script);
}

function clearAnalyticsCookies(): void {
  const names = document.cookie
    .split(";")
    .map((part) => part.trim().split("=")[0])
    .filter((name): name is string => Boolean(name?.startsWith("_ga")));
  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    if (
      window.location.hostname === "collectie.io" ||
      window.location.hostname.endsWith(".collectie.io")
    ) {
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.collectie.io; SameSite=Lax`;
    }
  }
}

export function initializeAnalytics(locale: Locale): void {
  if (getAnalyticsConsent() !== "granted") return;

  const page = buildAnalyticsPage(
    window.location,
    document.title,
    document.referrer,
    locale,
  );

  if (!analyticsInitialized) {
    gtag("consent", "default", analyticsDefaultConsent);
    gtag("consent", "update", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "granted",
    });
    gtag("js", new Date());
    gtag("config", analyticsMeasurementId, {
      ...analyticsConfig,
      page_location: page.pageLocation,
      page_referrer: page.pageReferrer,
    });
    appendGoogleTag();
    analyticsInitialized = true;
  } else {
    gtag("consent", "update", { analytics_storage: "granted" });
  }

  if (!pageViewSent) {
    gtag("event", "page_view", {
      page_title: page.pageTitle,
      page_location: page.pageLocation,
      page_path: page.pagePath,
      page_referrer: page.pageReferrer,
      language: page.language,
    });
    pageViewSent = true;
  }
}

export function grantAnalytics(locale: Locale): void {
  storeAnalyticsConsent("granted");
  initializeAnalytics(locale);
}

export function denyAnalytics(): void {
  storeAnalyticsConsent("denied");
  if (analyticsInitialized) {
    gtag("consent", "update", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    });
  }
  clearAnalyticsCookies();
}
